// Learn an opening: what you play, where it costs you, and every named
// opening there is. Pick a line and a course is built from the book.

import { useEffect, useMemo, useRef, useState } from "react";
import { errorText, getJson, isSignedOut, sendJson } from "../app/api.ts";
import { refreshDrillBadge } from "../app/badge.ts";
import { bookInfo, chosenBand } from "../app/book.ts";
import { cap, fmtCount, fmtScore, fold, pct } from "../app/format.ts";
import { Icon } from "../app/icons.tsx";
import { navigate } from "../app/router.ts";
import { toast } from "../app/toast.ts";
import { BackButton, type ScreenProps } from "../app/App.tsx";
import { Loading, Notice, ScreenHead, Seg } from "../app/ui.tsx";
import { Board } from "../board/Board.tsx";
import { BookPanel } from "../board/BookPanel.tsx";
import type { Score } from "../board/lines.ts";

interface Line {
  id: number;
  name: string;
  variation?: string;
  eco: string;
  pgn: string;
  fen: string;
  side?: "white" | "black" | null;
  gambit?: boolean;
  games?: number;
  white_score?: number | null;
  white_eval?: Score | null;
}

interface Family {
  family: string;
  games?: number;
  lines?: Line[];
}

interface MineLine {
  id?: number | null;
  name: string;
  eco?: string;
  games: number;
  mistakes: number;
  score?: number | null;
  repertoire?: { id: number; name: string } | null;
}

interface MineFamily extends MineLine {
  family: string;
  lines: MineLine[];
}

type Side = "white" | "black";

interface Suggestion {
  color: Side;
  gap: { sans: string[]; games: number; mistakes: number; score: number };
  line: { id: number; name: string; pgn: string; gambit: boolean };
  coverage: number;
  score: number;
  white_eval?: number | null;
  positions: number;
  others: { id: number; name: string; pgn: string; gambit: boolean; coverage: number; score: number }[];
}

interface Suggested {
  days: number;
  band_label: string;
  gambits_first: boolean;
  suggestions: Suggestion[];
}

/** Filters survive a trip into a course and back. */
const kept = { q: "", filter: "all", expanded: new Set<string>(), mineColor: "" as "" | Side, mineSort: "played", mineOpen: new Set<string>(), mineAll: false };

const FILTERS = [
  { value: "all", label: "All" },
  { value: "gambits", label: "Gambits" },
  { value: "white", label: "For White" },
  { value: "black", label: "For Black" },
];

// A family is a gambit by its own name. The server also marks a family that
// merely contains a gambit somewhere, which would put a Gambit badge on the
// King's Pawn Game.
const familyIsGambit = (family: Family) => /gambit/i.test(family.family);

/** A search that is all moves, as the catalogue writes them without their
 *  numbers ("e4 c5 b4"); null when any word of it is not a move. */
function movesOf(text: string): string | null {
  const words = text.replace(/\d+\.(\.\.)?|…/g, " ").split(/\s+/).filter(Boolean);
  const san = /^([kqrbn]?[a-h]?[1-8]?x?[a-h][1-8](=?[qrbn])?|o-o(-o)?)[+#]?$/;
  return words.length && words.every((w) => san.test(w)) ? words.join(" ") : null;
}

export function LearnScreen({ query }: ScreenProps) {
  // An opening puzzle links here by the opening's name, with the colour it
  // was played as: the line arrives selected, ready to be built.
  const wantedLine = query.get("line") || "";
  const wantedAs = (["white", "black"] as const).find((s) => s === query.get("as")) || null;
  const [catalog, setCatalog] = useState<{ families: Family[]; mine: { white?: MineFamily[]; black?: MineFamily[] } | null; bookAvailable: boolean } | null>(null);
  const [error, setError] = useState("");
  const [search, setSearch] = useState(kept.q);
  const [q, setQ] = useState(kept.q);
  const [filter, setFilter] = useState(kept.filter);
  const [limit, setLimit] = useState(40);
  const [selected, setSelected] = useState<{ line: Line; color: Side } | null>(null);
  const [suggested, setSuggested] = useState<Suggested | null>(null);
  const panel = useRef<HTMLElement>(null);

  useEffect(() => {
    (async () => {
      try {
        const info = await bookInfo();
        const band = chosenBand();
        const params = new URLSearchParams();
        if (band) params.set("band", band);
        const [data, played] = await Promise.all([getJson("/api/openings/catalog?" + params), getJson("/api/openings/mine").catch(() => null)]);
        const families: Family[] = data.families || [];
        setCatalog({ families, mine: played, bookAvailable: Boolean(info.available) });
        if (wantedLine) {
          // A name can belong to more than one move order; the one people
          // reach most is the one a game is likeliest to have been.
          const named = families
            .flatMap((f) => f.lines || [])
            .filter((l) => l.name === wantedLine)
            .sort((a, b) => (b.games || 0) - (a.games || 0));
          if (named[0]) setSelected({ line: named[0], color: wantedAs || named[0].side || "white" });
        }
      } catch (err) {
        if (!isSignedOut(err)) setError(errorText(err));
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Asked apart from the rest: reading your games against the book takes a
  // moment, and the catalogue need not wait for it.
  useEffect(() => {
    getJson("/api/openings/suggest")
      .then((data: Suggested) => setSuggested(data))
      .catch(() => setSuggested(null));
  }, []);

  useEffect(() => {
    const timer = setTimeout(() => {
      kept.q = search;
      setQ(search);
      setLimit(40);
    }, 120);
    return () => clearTimeout(timer);
  }, [search]);

  const lineById = useMemo(() => {
    const map = new Map<number, Line>();
    for (const f of catalog?.families || []) for (const l of f.lines || []) map.set(l.id, l);
    return map;
  }, [catalog]);

  const select = (line: Line, playAs?: Side | null) => {
    setSelected({ line, color: playAs || line.side || "white" });
    if (matchMedia("(max-width: 880px)").matches) requestAnimationFrame(() => panel.current?.scrollIntoView({ behavior: "smooth", block: "start" }));
  };

  return (
    <div className="screen">
      <BackButton meta={{ title: "Learn an opening", nav: "openings", back: "/openings", backLabel: "Openings" }} />
      <ScreenHead title="Learn an opening" subtitle="What you play, where it costs you, and every named opening there is. Pick a line and a course is built from the book." />
      {catalog && !catalog.bookAvailable ? (
        <div style={{ marginBottom: "14px" }}>
          <Notice kind="accent">The opening book is not installed on this server, so there are no statistics here and no courses — only the names.</Notice>
        </div>
      ) : null}
      <div className="learn">
        <div className="learn-list">
          {catalog && suggested?.suggestions.length ? <Suggestions suggested={suggested} lineById={lineById} onSelect={select} /> : null}
          {catalog?.mine ? <Mine mine={catalog.mine} lineById={lineById} onSelect={select} /> : null}
          <div className="learn-controls">
            <input
              className="field"
              type="search"
              placeholder="Search: name, ECO code or moves"
              autoComplete="off"
              spellCheck={false}
              value={search}
              onChange={(e) => setSearch(e.target.value)}
            />
            <div className="chips">
              {FILTERS.map((f) => (
                <button
                  key={f.value}
                  type="button"
                  className="chip"
                  aria-pressed={filter === f.value}
                  onClick={() => {
                    kept.filter = f.value;
                    setFilter(f.value);
                    setLimit(40);
                  }}
                >
                  {f.label}
                </button>
              ))}
            </div>
          </div>
          {error ? (
            <Notice kind="error">{error}</Notice>
          ) : !catalog ? (
            <Loading text="Reading the catalogue…" />
          ) : (
            <Catalogue families={catalog.families} q={q} filter={filter} limit={limit} onMore={() => setLimit((n) => n + 40)} selected={selected?.line.id} onSelect={select} />
          )}
        </div>
        <aside ref={panel} className={"card learn-panel" + (selected ? "" : " placeholder")}>
          {selected ? (
            <LinePanel
              key={selected.line.id}
              line={selected.line}
              color={selected.color}
              bookAvailable={Boolean(catalog?.bookAvailable)}
              onColor={(color) => setSelected({ ...selected, color })}
              onClose={() => setSelected(null)}
            />
          ) : (
            <p className="learn-empty">Choose a line to see it on the board, with what is played from it at your level and by masters.</p>
          )}
        </aside>
      </div>
    </div>
  );
}

function Catalogue({
  families,
  q,
  filter,
  limit,
  onMore,
  selected,
  onSelect,
}: {
  families: Family[];
  q: string;
  filter: string;
  limit: number;
  onMore: () => void;
  selected?: number;
  onSelect: (line: Line) => void;
}) {
  const [, redraw] = useState(0);
  const phrase = fold(q.trim());
  const terms = phrase.split(/\s+/).filter(Boolean);
  const asMoves = movesOf(phrase);
  const rank = (line: Line) => {
    if (asMoves) {
      const moves = movesOf(fold(line.pgn)) || "";
      if (moves === asMoves || moves.startsWith(asMoves + " ")) return 3;
      if ((" " + moves + " ").includes(" " + asMoves + " ")) return 2;
    }
    return phrase && fold(line.name).includes(phrase) ? 1 : 0;
  };
  const matches = (line: Line) => {
    if (filter === "gambits" && !line.gambit) return false;
    if (filter === "white" && line.side !== "white") return false;
    if (filter === "black" && line.side !== "black") return false;
    if (!terms.length) return true;
    const hay = fold(`${line.name} ${line.eco} ${line.pgn}`);
    return terms.every((t) => hay.includes(t));
  };
  const shown: { family: Family; lines: Line[]; exact: number }[] = [];
  let lineCount = 0;
  for (const family of families) {
    const lines = (family.lines || []).filter(matches);
    if (!lines.length) continue;
    lineCount += lines.length;
    // The line whose name holds the whole phrase goes first, and so does its
    // family: "king's gambit" is the King's Gambit before it is every King's
    // Pawn Game line that happens to have a gambit in it.
    // Moves searched for ("e4 c5 b4") find the lines that begin with them
    // first, then the ones that play them in a row later: every term found
    // somewhere is also the Evans Gambit, whose Bc5 holds a c5 and 4. b4 a b4.
    if (phrase) lines.sort((a, b) => rank(b) - rank(a));
    const best = lines.length ? Math.max(...lines.map(rank)) : 0;
    shown.push({ family, lines, exact: asMoves ? best : phrase && fold(family.family).includes(phrase) ? 2 : best });
  }
  if (phrase) shown.sort((a, b) => b.exact - a.exact);
  return (
    <>
      <div className="learn-count faint">
        {families.length
          ? `${fmtCount(lineCount)} ${lineCount === 1 ? "line" : "lines"} in ${fmtCount(shown.length)} ${shown.length === 1 ? "family" : "families"}`
          : ""}
      </div>
      <div className="families">
        {!shown.length ? (
          families.length ? (
            <div className="empty card">
              <h2>No openings match</h2>
              <p>Try fewer words, or another filter.</p>
            </div>
          ) : null
        ) : (
          <>
            {shown.slice(0, limit).map(({ family, lines }, index) => {
              const open = (terms.length > 0 && index < 8) || kept.expanded.has(family.family);
              return (
                <div key={family.family} className="family">
                  <button
                    type="button"
                    className="family-head"
                    aria-expanded={open}
                    onClick={() => {
                      if (open) kept.expanded.delete(family.family);
                      else kept.expanded.add(family.family);
                      redraw((n) => n + 1);
                    }}
                  >
                    <Icon name="chev" className="chev" />
                    <span className="family-title">
                      <span className="family-name">{family.family}</span>
                      {familyIsGambit(family) ? <span className="badge opening">Gambit</span> : null}
                    </span>
                    <span className="spacer" />
                    <span className="faint num family-meta">
                      <span className="family-count">
                        {lines.length} {lines.length === 1 ? "line" : "lines"}
                        {family.games ? " · " : ""}
                      </span>
                      {family.games ? `${fmtCount(family.games)} games` : null}
                    </span>
                  </button>
                  {open ? (
                    <div className="family-lines">
                      {lines.map((line) => (
                        <button key={line.id} type="button" className="line-row" aria-pressed={selected === line.id} data-line={line.id} onClick={() => onSelect(line)}>
                          <div className="line-main">
                            <div className="line-name">
                              {line.variation || line.name}
                              {line.gambit ? <span className="dot-gambit" title="Gambit" /> : null}
                            </div>
                            <div className="line-moves">
                              {line.eco} · {line.pgn}
                            </div>
                          </div>
                          <div className="line-side num">
                            {line.games ? <span>{fmtCount(line.games)}</span> : <span className="faint">—</span>}
                            <span className="faint">
                              {[line.white_score != null ? `W ${pct(line.white_score)}` : "", line.white_eval ? fmtScore(line.white_eval) : ""].filter(Boolean).join(" · ")}
                            </span>
                          </div>
                        </button>
                      ))}
                    </div>
                  ) : null}
                </div>
              );
            })}
            {shown.length > limit ? (
              <button type="button" className="btn btn-block learn-more" onClick={onMore}>
                Show more families ({shown.length - limit} left)
              </button>
            ) : null}
          </>
        )}
      </div>
    </>
  );
}

function LinePanel({
  line,
  color,
  bookAvailable,
  onColor,
  onClose,
}: {
  line: Line;
  color: Side;
  bookAvailable: boolean;
  onColor: (color: Side) => void;
  onClose: () => void;
}) {
  const [building, setBuilding] = useState(false);
  const [problem, setProblem] = useState("");
  async function build() {
    setBuilding(true);
    setProblem("");
    try {
      const body: Record<string, unknown> = { line: line.id, color };
      if (chosenBand()) body.band = chosenBand();
      const data = await sendJson("POST", "/api/openings/courses", body);
      refreshDrillBadge(true);
      if (data.existing) toast(`You already have a ${data.repertoire.name} course as ${cap(color)} — here it is.`);
      navigate(`/openings/${data.repertoire.id}`);
    } catch (err) {
      setBuilding(false);
      if (!isSignedOut(err)) setProblem(errorText(err));
    }
  }
  return (
    <>
      <div className="learn-panel-head">
        <h2>{line.name}</h2>
        <div className="line-moves">
          {line.eco} · {line.pgn}
        </div>
        <button type="button" className="btn btn-ghost btn-sm learn-close" aria-label="Close" onClick={onClose}>
          Close
        </button>
      </div>
      <Board className="view-board" fen={line.fen} orientation={color} />
      {/* The action first, the reference table under it: the table runs to
          fifteen moves, and on a phone it pushed the button off the screen. */}
      <div className="learn-build">
        <span className="label">Play it as</span>
        <Seg<Side>
          className="block"
          options={[
            { value: "white", label: "White" },
            { value: "black", label: "Black" },
          ]}
          value={color}
          onChange={onColor}
        />
        <p className="help">
          {line.side
            ? `${cap(line.side)} chooses this line. The course prepares your moves, the replies you will meet at your level, and the traps.`
            : "The course prepares your moves, the replies you will meet at your level, and the traps."}
        </p>
        <button type="button" className="btn btn-primary btn-block" disabled={!bookAvailable || building} onClick={() => void build()}>
          <Icon name="book" />
          Build course
        </button>
        {bookAvailable ? null : <p className="help">Courses need the opening book, which is not installed on this server.</p>}
        {building ? <Loading text="Building the course from the book…" /> : null}
        {problem ? <Notice kind="error">{problem}</Notice> : null}
      </div>
      <BookPanel fen={line.fen} />
    </>
  );
}

/** "1. d4" or "1. e4 e6": the moves that reached a position. */
function movesText(sans: string[]): string {
  return sans.map((san, i) => (i % 2 === 0 ? `${i / 2 + 1}. ${san}` : san)).join(" ");
}

/** Openings to learn next: where your games go with nothing prepared, and a
 *  line to meet it, gambits first below master strength. Above what you play,
 *  because it is what to do about it. */
function Suggestions({ suggested, lineById, onSelect }: { suggested: Suggested; lineById: Map<number, Line>; onSelect: (line: Line, color: Side) => void }) {
  const months = Math.round(suggested.days / 30);
  const period = months >= 12 ? "the last year" : `the last ${months} months`;
  return (
    <section className="card mine suggest">
      <div className="rep-list-head">
        <h2>Suggested for you</h2>
      </div>
      <p className="help">
        Where your games of {period} go with nothing prepared, and a line to meet it that scores at {suggested.band_label}.{" "}
        {suggested.gambits_first
          ? "At your level a gambit comes first: it pays while the opponent has to find the answer over the board."
          : "At your level the soundest line comes first: strong opponents find the answer to a gambit."}
      </p>
      <div className="mine-lines suggest-lines">
        {suggested.suggestions.map((s) => {
          const target = lineById.get(s.line.id);
          const against = s.gap.sans.length % 2 === 1 ? `As Black against ${movesText(s.gap.sans)}` : `As White after ${movesText(s.gap.sans)}`;
          return (
            <div key={s.line.id} className="mine-line suggest-line">
              <button
                type="button"
                className="mine-line-main"
                disabled={!target}
                title={target ? "See it on the board, and build a course for it" : undefined}
                onClick={() => target && onSelect(target, s.color)}
              >
                <div className="line-moves suggest-gap">
                  {against} · {s.gap.games} games with nothing prepared · you score {pct(s.gap.score)}
                </div>
                <div className="line-name">
                  {s.line.name}
                  {s.line.gambit ? <span className="badge opening suggest-badge">Gambit</span> : null}
                </div>
                <div className="line-moves">{s.line.pgn}</div>
                <div className="line-moves suggest-facts">
                  {[
                    `meets ${pct(s.coverage)} of those games`,
                    `scores ${pct(s.score)} at ${suggested.band_label}`,
                    s.white_eval != null ? fmtScore({ kind: "cp", value: s.white_eval }) : "",
                    s.positions ? `${s.positions} positions to learn` : "",
                  ]
                    .filter(Boolean)
                    .join(" · ")}
                </div>
              </button>
              <div className="mine-line-side">
                {target ? (
                  <button type="button" className="btn btn-sm" onClick={() => onSelect(target, s.color)}>
                    <Icon name="book" />
                    Study
                  </button>
                ) : null}
              </div>
              {s.others.length ? (
                <div className="suggest-others">
                  Or{" "}
                  {s.others.map((o, i) => {
                    const other = lineById.get(o.id);
                    return (
                      <span key={o.id}>
                        {i ? ", " : ""}
                        <button type="button" className="link-btn" disabled={!other} onClick={() => other && onSelect(other, s.color)}>
                          {o.name}
                        </button>
                      </span>
                    );
                  })}
                </div>
              ) : null}
            </div>
          );
        })}
      </div>
    </section>
  );
}

/** What you actually play, above the catalogue of everything: the openings
 *  from your own games, most played first, with the mistakes made in them. */
function Mine({ mine, lineById, onSelect }: { mine: { white?: MineFamily[]; black?: MineFamily[] }; lineById: Map<number, Line>; onSelect: (line: Line, color: Side) => void }) {
  const [, redraw] = useState(0);
  const refresh = () => redraw((n) => n + 1);
  const sides = { white: mine.white || [], black: mine.black || [] };
  if (!sides.white.length && !sides.black.length) return null;
  const gamesOf = (side: Side) => sides[side].reduce((n, f) => n + f.games, 0);
  if (!kept.mineColor) kept.mineColor = gamesOf("white") >= gamesOf("black") ? "white" : "black";
  const color = kept.mineColor;
  const sort = kept.mineSort;
  const families = sides[color].slice();
  // "Most mistakes" ranks by how many, not by rate: one bad game in one game
  // is not the opening to study next.
  if (sort === "mistakes") families.sort((a, b) => b.mistakes - a.mistakes || b.games - a.games);
  const rate = (x: MineLine) => (x.games ? x.mistakes / x.games : 0);
  const Mistakes = ({ x }: { x: MineLine }) =>
    x.mistakes ? (
      <span className={"count " + (x.games >= 3 && rate(x) >= 0.25 ? "blunder" : "neutral")} title="Opening mistakes: moves inside theory that cost you">
        {x.mistakes} {x.mistakes === 1 ? "mistake" : "mistakes"} · {pct(rate(x))}
      </span>
    ) : (
      <span className="count clean">no mistakes</span>
    );
  const sub = (x: MineLine) => [`${x.games} ${x.games === 1 ? "game" : "games"}`, x.score != null ? `you score ${pct(x.score)}` : ""].filter(Boolean).join(" · ");
  const HEAD = 8;
  const shown = kept.mineAll ? families : families.slice(0, HEAD);
  return (
    <section className="card mine">
      <div className="rep-list-head">
        <h2>Your openings</h2>
        <Seg<Side>
          options={[
            { value: "white", label: `As White ${fmtCount(gamesOf("white"))}` },
            { value: "black", label: `As Black ${fmtCount(gamesOf("black"))}` },
          ]}
          value={color}
          onChange={(v) => {
            kept.mineColor = v;
            refresh();
          }}
        />
      </div>
      <p className="help">From your analysed games, most played first. A mistake is a move inside theory that cost you: an opening puzzle in Train.</p>
      <div className="chips">
        {[
          ["played", "Most played"],
          ["mistakes", "Most mistakes"],
        ].map(([value, label]) => (
          <button
            key={value}
            type="button"
            className="chip"
            aria-pressed={sort === value}
            onClick={() => {
              kept.mineSort = value!;
              refresh();
            }}
          >
            {label}
          </button>
        ))}
      </div>
      <div className="mine-families">
        {shown.map((f) => {
          const key = `${color}:${f.family}`;
          const open = kept.mineOpen.has(key);
          return (
            <div key={key} className={"mine-family" + (open ? " open" : "")}>
              <button
                type="button"
                className="mine-head"
                aria-expanded={open}
                onClick={() => {
                  if (open) kept.mineOpen.delete(key);
                  else kept.mineOpen.add(key);
                  refresh();
                }}
              >
                <div className="mine-head-main">
                  <div className="line-name">{f.family}</div>
                  <div className="line-moves">{sub(f)}</div>
                </div>
                <div className="mine-head-side">
                  <Mistakes x={f} />
                  <Icon name="chev" className="chev" />
                </div>
              </button>
              {open ? (
                <div className="mine-lines">
                  {f.lines.map((line, i) => {
                    const target = line.id != null ? lineById.get(line.id) : undefined;
                    return (
                      <div key={i} className="mine-line">
                        <button
                          type="button"
                          className="mine-line-main"
                          disabled={!target}
                          title={target ? "See it on the board, and build a course for it" : undefined}
                          onClick={() => target && onSelect(target, color)}
                        >
                          <div className="line-name">
                            {line.name}
                            {line.eco ? <span className="faint"> {line.eco}</span> : null}
                          </div>
                          <div className="line-moves">{sub(line)}</div>
                        </button>
                        <div className="mine-line-side">
                          <Mistakes x={line} />
                          {line.repertoire ? (
                            <button type="button" className="btn btn-sm" title={`Open ${line.repertoire.name}`} onClick={() => navigate(`/openings/${line.repertoire!.id}`)}>
                              <Icon name="branch" />
                              Course
                            </button>
                          ) : target ? (
                            <button type="button" className="btn btn-sm" onClick={() => onSelect(target, color)}>
                              <Icon name="book" />
                              Study
                            </button>
                          ) : null}
                        </div>
                      </div>
                    );
                  })}
                </div>
              ) : null}
            </div>
          );
        })}
      </div>
      {families.length > HEAD && !kept.mineAll ? (
        <button
          type="button"
          className="link-btn rep-more"
          onClick={() => {
            kept.mineAll = true;
            refresh();
          }}
        >
          Show {families.length - HEAD} more
        </button>
      ) : null}
    </section>
  );
}
